import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { PageHeader } from '../../components/layout/PageHeader';
import { Combobox } from '../../components/ui/Combobox';
import { Label } from '../../components/ui/label';
import { listAccounts } from '../accounts';
import { useCurrentUser } from '../auth';
import { BotSetup } from './BotSetup';

// Bot settings: the standalone page (a client picker, then that client's bot setup). The same BotSetup is hosted by the account's
// KyckConnect -> Setup -> AI SMS step. The names the old file exported stay exported from here (tests and the bot form import them).
export { BotConfigForm, BotSetup, toCapNumber } from './BotSetup';

export function BotSettingsPage() {
  const [accountId, setAccountId] = useState('');
  const me = useCurrentUser();

  const { data: accounts = [] } = useQuery({ queryKey: ['accounts'], queryFn: listAccounts });

  const accountOptions = useMemo(
    () =>
      accounts
        .filter((a) => a.status !== 'cancelled')
        .map((a) => ({ value: a.id, label: a.name }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [accounts],
  );

  return (
    <div>
      <PageHeader
        title="Bot settings"
        description="The knowledge base the bot answers from, and how each client's bot behaves."
        backHref="/kyckconnect/approvals"
      />

      <div className="mb-6 max-w-md">
        <Label htmlFor="settings-account">Client</Label>
        <Combobox
          id="settings-account"
          value={accountId}
          onValueChange={setAccountId}
          options={accountOptions}
          placeholder="Select a client"
          searchPlaceholder="Search clients…"
          emptyText="No client matches."
        />
      </div>

      {accountId === '' ? (
        <p className="text-sm text-muted-foreground">
          Pick a client to edit its knowledge base and bot.
        </p>
      ) : (
        <BotSetup
          accountId={accountId}
          accountName={accountOptions.find((a) => a.value === accountId)?.label ?? ''}
          accountIndustry={accounts.find((a) => a.id === accountId)?.industry ?? null}
          isAdmin={me.role === 'admin'}
        />
      )}
    </div>
  );
}
